import { useState, useEffect, useCallback, useRef } from "react";
import * as api from "@/lib/api";
import { logger } from "@/lib/logger";
import { onForegroundMessage } from "@/lib/firebase";

const POLL_INTERVAL_MS = 30_000; // 30 seconds

export interface Notification {
  id: string;
  type: string;
  title: string;
  body: string;
  data: Record<string, string> | null;
  read_at: string | null;
  created_at: string;
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.getNotifications();
      setNotifications(res.notifications);
    } catch (e) {
      // Silently fail -- the bell just won't update
      if (e instanceof api.ApiError && e.status !== 401) {
        logger.debug("Failed to fetch notifications");
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch + polling
  useEffect(() => {
    fetchNotifications();

    intervalRef.current = setInterval(fetchNotifications, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchNotifications]);

  // Listen for foreground FCM messages -- triggers immediate refetch
  useEffect(() => {
    const unsubscribe = onForegroundMessage(() => {
      fetchNotifications();
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [fetchNotifications]);

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  const markAsRead = useCallback(
    async (id: string) => {
      try {
        await api.markNotificationRead(id);
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === id ? { ...n, read_at: new Date().toISOString() } : n,
          ),
        );
      } catch (e) {
        logger.debug("Failed to mark notification as read");
      }
    },
    [],
  );

  const markAllRead = useCallback(async () => {
    try {
      await api.markAllNotificationsRead();
      const now = new Date().toISOString();
      setNotifications((prev) =>
        prev.map((n) => (n.read_at ? n : { ...n, read_at: now })),
      );
    } catch (e) {
      logger.debug("Failed to mark all notifications as read");
    }
  }, []);

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllRead,
    isLoading,
  };
}
